// src/products/dataroom/pages/DataroomQuestionsPage.js
// Page to show all questions for all dataroom projects with filtering
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Clock, CheckCircle, AlertCircle, Plus } from 'lucide-react';
import simpleQuestionsApi from '../../questions/services/simpleQuestionsApi';
import ContextAwareQuestionForm from '../../questions/components/ContextAwareQuestionForm';
import { formatDate } from '../../questions/utils';

const DataroomQuestionsPage = ({ projectId, projectName, user, userRole, projects }) => {
  const navigate = useNavigate();
  const [allQuestions, setAllQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [groupedQuestions, setGroupedQuestions] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showQuestionForm, setShowQuestionForm] = useState(false);

  // Helper function to get project name by ID
  const getProjectName = useCallback((projectId) => {
    const project = projects?.find(p => p.id === projectId);
    return project?.project_name || `Project ${projectId}`;
  }, [projects]);

  // Load all dataroom questions
  useEffect(() => {
    const loadAllQuestions = async () => {
      setLoading(true);
      try {
        const response = await simpleQuestionsApi.getQuestions({
          module_type: 'dataroom'
        });
        
        if (response.success) {
          // Ensure we only show dataroom questions, filter out any general questions
          const dataroomQuestions = (response.data || []).filter(q => q.module_type === 'dataroom');
          setAllQuestions(dataroomQuestions);
          setFilteredQuestions(dataroomQuestions);
        }
      } catch (error) {
        console.error('Error loading questions:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAllQuestions();
  }, []);

  // Filter questions based on selected project and search
  useEffect(() => {
    console.log('DataroomQuestionsPage - projectId:', projectId);
    console.log('DataroomQuestionsPage - allQuestions count:', allQuestions.length);
    
    let filtered = allQuestions;

    // Filter by selected project if one is selected
    if (projectId) {
      filtered = filtered.filter(q => q.project_id === projectId);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(q => 
        q.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.category?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(q => q.status === statusFilter);
    }

    setFilteredQuestions(filtered);

    // Group questions by dealroom (only when not in a specific project)
    if (!projectId) {
      console.log('Grouping questions by dealroom...');
      const grouped = {};
      
      // Get all unique project IDs from filtered questions
      const projectIds = [...new Set(filtered.map(q => q.project_id))];
      
      // Create groups for each project
      projectIds.forEach(pid => {
        const projectQuestions = filtered.filter(q => q.project_id === pid);
        const projectName = getProjectName(pid);
        grouped[projectName] = {
          projectId: pid,
          questions: projectQuestions
        };
      });

      // Also include projects with no questions (from projects list)
      if (projects && projects.length > 0) {
        projects.forEach(project => {
          const projectName = project.project_name;
          if (!grouped[projectName]) {
            grouped[projectName] = {
              projectId: project.id,
              questions: []
            };
          }
        });
      }

      console.log('Grouped questions:', grouped);
      console.log('Number of dealrooms:', Object.keys(grouped).length);
      setGroupedQuestions(grouped);
    } else {
      console.log('Skipping grouping (projectId is set)');
      setGroupedQuestions({});
    }
  }, [allQuestions, projectId, searchTerm, statusFilter, projects, getProjectName]);

  // Status configuration (matching SimpleQuestionsPage)
  const STATUS_CONFIG = {
    open: { 
      bg: 'bg-orange-50 border-orange-200', 
      text: 'text-orange-800', 
      label: 'Open',
      icon: Clock
    },
    answered: { 
      bg: 'bg-green-50 border-green-200', 
      text: 'text-green-800', 
      label: 'Answered',
      icon: CheckCircle
    },
    duplicate: { 
      bg: 'bg-purple-50 border-purple-200', 
      text: 'text-purple-800', 
      label: 'Duplicate',
      icon: AlertCircle
    },
    out_of_scope: { 
      bg: 'bg-gray-50 border-gray-200', 
      text: 'text-gray-800', 
      label: 'Out of Scope',
      icon: AlertCircle
    },
    closed: { 
      bg: 'bg-blue-50 border-blue-200', 
      text: 'text-blue-800', 
      label: 'Closed',
      icon: CheckCircle
    }
  };

  // Get status configuration
  const getStatusConfig = (status) => {
    return STATUS_CONFIG[status] || STATUS_CONFIG.open;
  };

  // Get priority color (matching SimpleQuestionsPage)
  const getPriorityColor = (priority) => {
    if (priority === 'high') {
      return 'bg-red-50 text-red-800 border-red-200';
    }
    return 'bg-gray-50 text-gray-800 border-gray-200';
  };

  // Get context color (matching SimpleQuestionsPage)
  const getContextColor = (moduleType) => {
    switch (moduleType) {
      case 'dataroom':
        return 'bg-blue-50 text-blue-800 border-blue-200';
      case 'capraise':
        return 'bg-green-50 text-green-800 border-green-200';
      case 'equity_manager':
        return 'bg-purple-50 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-50 text-gray-800 border-gray-200';
    }
  };


  // Handle question click to navigate to detail
  const handleQuestionClick = (questionId) => {
    navigate(`/questions/detail/${questionId}`);
  };

  const handleQuestionCreated = (newQuestion) => {
    setAllQuestions(prev => [newQuestion, ...prev]);
    setShowQuestionForm(false);
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Deal Questions</h1>
            <p className="text-gray-600 mt-1">
              {projectId 
                ? `Ask questions about ${projectName} documents and deal-specific information` 
                : 'View all deal questions across your projects'
              }
            </p>
            {projectId && (
              <p className="text-sm text-blue-600 mt-1">
                Questions are visible to the Creator and members of your Party/Team.
              </p>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {projectId && (
              <button
                onClick={() => setShowQuestionForm(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Submit Deal Question
              </button>
            )}
            <button
              onClick={() => navigate('/dealroom')}
              className="inline-flex items-center px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to DealRooms
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search Dealroom Questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="answered">Answered</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {/* Questions List */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white rounded-lg border border-gray-200 text-center py-8">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-sm text-gray-500 mt-2">Loading Dealroom Questions...</p>
          </div>
        ) : projectId ? (
          /* Show flat list when viewing specific project */
          filteredQuestions.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 text-center py-12">
              <div className="h-12 w-12 text-gray-400 mx-auto mb-4">💬</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No questions found</h3>
              <p className="text-sm text-gray-500 mb-4">
                {searchTerm || statusFilter !== 'all'
                  ? 'No questions match your current filters' 
                  : 'No questions have been asked yet'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredQuestions.map((question) => (
                <div 
                  key={question.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleQuestionClick(question.id)}
                >
                  {/* Metadata Header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      {/* Date */}
                      <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                        {formatDate(question.created_at)}
                      </span>
                      
                      {/* Priority */}
                      <span className={`text-xs px-2 py-1 rounded border ${getPriorityColor(question.priority)}`}>
                        {question.priority === 'high' ? 'High Priority' : 'Normal'}
                      </span>
                      
                      {/* Status */}
                      {(() => {
                        const statusConfig = getStatusConfig(question.status);
                        const StatusIcon = statusConfig.icon;
                        return (
                          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${statusConfig.bg} ${statusConfig.text}`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusConfig.label}
                          </span>
                        );
                      })()}
                      
                      {/* Context */}
                      <span className={`text-xs px-2 py-1 rounded border ${getContextColor(question.module_type)}`}>
                        {question.module_type === 'equity_manager' ? 'Equity' : 
                         question.module_type === 'dataroom' ? 'Dealroom' :
                         question.module_type === 'capraise' ? 'CapRaise' : 'General'}
                      </span>
                    </div>
                  </div>

                  {/* Question Content */}
                  <div className="pl-0">
                    <h3 className="text-base font-semibold text-gray-900 mb-1 line-clamp-1">
                      {question.title}
                    </h3>
                    
                    {/* Dealroom Context Path */}
                    <div className="text-sm text-blue-600 mb-2">
                      {getProjectName(question.project_id)}
                      {question.folder_name && ` > ${question.folder_name}`}
                      {question.file_name && ` > ${question.file_name}`}
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                      {question.description}
                    </p>
                    
                    {/* Footer Info */}
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center space-x-3">
                        <span>{question.user_name || 'Unknown'}</span>
                        <span>•</span>
                        <span>{question.category}</span>
                        <span>•</span>
                        <span>{formatDate(question.created_at)}</span>
                      </div>
                      
                      {/* Response Count */}
                      {question.responses && question.responses.length > 0 && (
                        <span className="text-blue-600 font-medium">
                          {question.responses.length} response{question.responses.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          /* Show grouped by dealroom when viewing all projects */
          Object.keys(groupedQuestions).length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 text-center py-12">
              <div className="h-12 w-12 text-gray-400 mx-auto mb-4">💬</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Dealroom Questions found</h3>
              <p className="text-sm text-gray-500 mb-4">
                {searchTerm || statusFilter !== 'all'
                  ? 'No questions match your current filters' 
                  : 'No questions have been asked yet'
                }
              </p>
            </div>
          ) : (
            Object.entries(groupedQuestions)
              .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
              .map(([dealroomName, { projectId: pid, questions: projectQuestions }]) => (
                <div key={pid} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  {/* Dealroom Header */}
                  <div className="bg-blue-50 border-b border-blue-200 px-4 py-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-blue-900">{dealroomName}</h3>
                      <span className="text-sm text-blue-700">
                        {projectQuestions.length} {projectQuestions.length === 1 ? 'question' : 'questions'}
                      </span>
                    </div>
                  </div>

                  {/* Questions for this dealroom */}
                  {projectQuestions.length === 0 ? (
                    <div className="px-4 py-8 text-center text-gray-500 text-sm">
                      No questions submitted for this dealroom
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {projectQuestions.map((question) => (
                        <div 
                          key={question.id} 
                          className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => handleQuestionClick(question.id)}
                        >
                          {/* Metadata Header */}
                          <div className="flex items-center space-x-2 mb-2">
                            {/* Date */}
                            <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                              {formatDate(question.created_at)}
                            </span>
                            
                            {/* Priority */}
                            {question.priority === 'high' && (
                              <span className="text-xs px-2 py-1 rounded border bg-red-50 text-red-800 border-red-200">
                                High Priority
                              </span>
                            )}
                            
                            {/* Status */}
                            {(() => {
                              const statusConfig = getStatusConfig(question.status);
                              const StatusIcon = statusConfig.icon;
                              return (
                                <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${statusConfig.bg} ${statusConfig.text}`}>
                                  <StatusIcon className="h-3 w-3 mr-1" />
                                  {statusConfig.label}
                                </span>
                              );
                            })()}
                          </div>

                          {/* Question Content */}
                          <div>
                            <h4 className="text-base font-semibold text-gray-900 mb-1 line-clamp-1">
                              {question.title}
                            </h4>
                            
                            {/* Folder/File Context */}
                            {(question.folder_name || question.file_name) && (
                              <div className="text-sm text-blue-600 mb-2">
                                {question.folder_name && `${question.folder_name}`}
                                {question.file_name && ` > ${question.file_name}`}
                              </div>
                            )}
                            
                            <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                              {question.description}
                            </p>
                            
                            {/* Footer Info */}
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <div className="flex items-center space-x-3">
                                <span>{question.user_name || 'Unknown'}</span>
                                <span>•</span>
                                <span>{question.category}</span>
                              </div>
                              
                              {/* Response Count */}
                              {question.responses && question.responses.length > 0 && (
                                <span className="text-blue-600 font-medium">
                                  {question.responses.length} response{question.responses.length !== 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
          )
        )}
      </div>

      {/* Question Form Modal */}
      {showQuestionForm && (
        <ContextAwareQuestionForm
          isOpen={showQuestionForm}
          onClose={() => setShowQuestionForm(false)}
          context={{
            moduleType: 'dataroom',
            moduleReferenceId: projectId,
            moduleReferenceName: projectName,
            projectId: projectId,
            companyId: null
          }}
          onSubmit={handleQuestionCreated}
          user={user}
        />
      )}
    </div>
  );
};

export default DataroomQuestionsPage;